import { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Sparkles, Loader2, Zap, Flame, TrendingUp, Euro, Activity, Trophy, ArrowRight, Calendar, FileImage, BadgeCheck, ScanLine } from "lucide-react";
import { useAuth } from "@/lib/auth";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import { Link } from "react-router-dom";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysForMany } from "@/config/cmsSections";

// Stable chart styles — defined at module scope to avoid creating new object refs on every render.
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid hsl(152, 9%, 85%)" };

export default function Dashboard() {
    const { user } = useAuth();
    const c = useSiteContent(keysForMany("common", "dashboard"));
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [form, setForm] = useState({ month: "", kwh: "", cost_eur: "", energy_type: "electricity" });
    const [saving, setSaving] = useState(false);

    const [insights, setInsights] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [view, setView] = useState("electricity");

    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);

    const [bills, setBills] = useState([]);
    const [billsLoading, setBillsLoading] = useState(true);

    const loadBills = useCallback(async () => {
        setBillsLoading(true);
        try {
            const { data } = await api.get("/me/bills");
            setBills(data);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") console.error("loadBills failed", e);
        } finally {
            setBillsLoading(false);
        }
    }, []);

    const deleteBill = async (bid) => {
        try {
            await api.delete(`/me/bills/${bid}`);
            setBills((arr) => arr.filter((b) => b.id !== bid));
            toast.success("Analyse supprimée");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const loadHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const { data } = await api.get("/quiz-history");
            setHistory(data);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") console.error("loadHistory failed", e);
        } finally {
            setHistoryLoading(false);
        }
    }, []);

    const deleteHistory = async (hid) => {
        try {
            await api.delete(`/quiz-history/${hid}`);
            setHistory((arr) => arr.filter((h) => h.id !== hid));
            toast.success("Recommandation supprimée");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const loadItems = useCallback(async () => {
        try {
            const { data } = await api.get("/consumption");
            setItems(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadItems();
        loadHistory();
        loadBills();
    }, [loadItems, loadHistory, loadBills]);

    const addItem = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/consumption", {
                month: form.month,
                kwh: Number(form.kwh),
                cost_eur: Number(form.cost_eur),
                energy_type: form.energy_type,
            });
            toast.success("Mesure ajoutée");
            setForm({ month: "", kwh: "", cost_eur: "", energy_type: "electricity" });
            setOpenDialog(false);
            await loadItems();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteItem = async (id) => {
        try {
            await api.delete(`/consumption/${id}`);
            toast.success("Mesure supprimée");
            setItems((arr) => arr.filter((i) => i.id !== id));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const runInsights = async () => {
        setInsightsLoading(true);
        try {
            const { data } = await api.get("/ai/insights");
            setInsights(data.insights);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setInsightsLoading(false);
        }
    };

    const filtered = items.filter((i) => i.energy_type === view).sort((a, b) => a.month.localeCompare(b.month));
    const totalKwh = filtered.reduce((s, i) => s + i.kwh, 0);
    const totalCost = filtered.reduce((s, i) => s + i.cost_eur, 0);
    const avgMonth = filtered.length ? totalCost / filtered.length : 0;

    return (
        <div className="em-container py-12" data-testid="dashboard-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="em-eyebrow">{c("dashboard.hero.eyebrow", "Espace client")}</p>
                    <h1 className="font-display text-4xl md:text-5xl font-black text-primary mt-2">
                        {c("dashboard.greeting_prefix", "Bonjour")}, {user?.name?.split(" ")[0] || "client"}
                    </h1>
                    <p className="text-muted-foreground mt-2">{c("dashboard.hero.subtitle", "Pilotez votre consommation et obtenez des conseils personnalisés.")}</p>
                </div>
                <div className="flex gap-3">
                    <Dialog open={openDialog} onOpenChange={setOpenDialog}>
                        <DialogTrigger asChild>
                            <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="add-consumption-btn">
                                <Plus className="h-4 w-4 mr-2" />{c("dashboard.add_btn", "Ajouter une mesure")}
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-display">{c("dashboard.dialog.title", "Nouvelle mesure")}</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={addItem} className="space-y-4" data-testid="consumption-form">
                                <div>
                                    <Label>{c("dashboard.dialog.label_type", "Type")}</Label>
                                    <Select value={form.energy_type} onValueChange={(v) => setForm({ ...form, energy_type: v })}>
                                        <SelectTrigger className="rounded-xl mt-2" data-testid="consumption-type">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="electricity">{c("common.energy.elec", "Électricité")}</SelectItem>
                                            <SelectItem value="gas">{c("common.energy.gas", "Gaz")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label htmlFor="month">{c("dashboard.dialog.label_month", "Mois (AAAA-MM)")}</Label>
                                    <Input id="month" type="month" required value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="rounded-xl mt-2" data-testid="consumption-month" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label htmlFor="kwh">{c("dashboard.dialog.label_kwh", "Consommation (kWh)")}</Label>
                                        <Input id="kwh" type="number" required value={form.kwh} onChange={(e) => setForm({ ...form, kwh: e.target.value })} className="rounded-xl mt-2" data-testid="consumption-kwh" />
                                    </div>
                                    <div>
                                        <Label htmlFor="cost">{c("dashboard.dialog.label_cost", "Coût (€)")}</Label>
                                        <Input id="cost" type="number" step="0.01" required value={form.cost_eur} onChange={(e) => setForm({ ...form, cost_eur: e.target.value })} className="rounded-xl mt-2" data-testid="consumption-cost" />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button type="submit" disabled={saving} className="rounded-full bg-primary hover:bg-primary/90" data-testid="consumption-submit">
                                        {saving ? "Enregistrement…" : c("dashboard.dialog.submit", "Enregistrer")}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* KPI ROW */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10" data-testid="dashboard-kpis">
                <Card className="rounded-3xl border-border">
                    <CardContent className="p-6 flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center"><Activity className="h-6 w-6 text-accent-foreground" /></div>
                        <div>
                            <p className="em-eyebrow">{c("dashboard.kpi.cumulated", "Conso cumulée")}</p>
                            <p className="font-display text-3xl font-black text-primary">{totalKwh.toFixed(0)} kWh</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="rounded-3xl border-border">
                    <CardContent className="p-6 flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center"><Euro className="h-6 w-6 text-accent-foreground" /></div>
                        <div>
                            <p className="em-eyebrow">{c("dashboard.kpi.cost", "Coût total")}</p>
                            <p className="font-display text-3xl font-black text-primary">{totalCost.toFixed(0)} €</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="rounded-3xl border-border">
                    <CardContent className="p-6 flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-accent flex items-center justify-center"><TrendingUp className="h-6 w-6 text-accent-foreground" /></div>
                        <div>
                            <p className="em-eyebrow">{c("dashboard.kpi.avg", "Moyenne mensuelle")}</p>
                            <p className="font-display text-3xl font-black text-primary">{avgMonth.toFixed(0)} €</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* CHART + AI */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                <Card className="rounded-3xl border-border lg:col-span-2">
                    <CardContent className="p-7">
                        <div className="flex items-center justify-between mb-5">
                            <div>
                                <p className="em-eyebrow">{c("dashboard.chart.eyebrow", "Suivi consommation")}</p>
                                <h3 className="font-display text-xl font-bold text-primary mt-1">{c("dashboard.chart.title", "Évolution mensuelle")}</h3>
                            </div>
                            <Tabs value={view} onValueChange={setView}>
                                <TabsList className="rounded-full">
                                    <TabsTrigger value="electricity" className="rounded-full gap-2" data-testid="dashboard-tab-electricity"><Zap className="h-4 w-4" />Élec.</TabsTrigger>
                                    <TabsTrigger value="gas" className="rounded-full gap-2" data-testid="dashboard-tab-gas"><Flame className="h-4 w-4" />Gaz</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                        {filtered.length === 0 ? (
                            <div className="bg-secondary rounded-2xl p-10 text-center text-muted-foreground" data-testid="chart-empty">
                                {c("dashboard.chart.empty", "Ajoutez vos premières mesures pour voir l'évolution de votre consommation au fil des mois.")}
                            </div>
                        ) : (
                            <div className="h-[280px]" data-testid="consumption-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={filtered}>
                                        <defs>
                                            <linearGradient id="colorKwh" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor="hsl(154, 71%, 14%)" stopOpacity={0.55} />
                                                <stop offset="100%" stopColor="hsl(154, 71%, 14%)" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke="hsl(152, 9%, 90%)" strokeDasharray="3 3" />
                                        <XAxis dataKey="month" stroke="hsl(162, 7%, 38%)" fontSize={12} />
                                        <YAxis stroke="hsl(162, 7%, 38%)" fontSize={12} />
                                        <Tooltip contentStyle={TOOLTIP_STYLE} />
                                        <Area type="monotone" dataKey="kwh" stroke="hsl(154, 71%, 14%)" strokeWidth={2.5} fill="url(#colorKwh)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="rounded-3xl bg-primary text-primary-foreground border-0">
                    <CardContent className="p-7">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="h-5 w-5 text-accent" />
                            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">{c("dashboard.ai.eyebrow", "Insight")}</p>
                        </div>
                        <h3 className="font-display text-xl font-bold">{c("dashboard.ai.title", "Conseil personnalisé")}</h3>
                        <p className="text-primary-foreground/80 text-sm mt-2 leading-relaxed">
                            {c("dashboard.ai.body", "Notre moteur analyse vos consommations pour identifier les leviers d'économies les plus pertinents pour votre profil.")}
                        </p>
                        <Button
                            onClick={runInsights}
                            disabled={insightsLoading}
                            className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold mt-5 w-full"
                            data-testid="ai-insights-btn"
                        >
                            {insightsLoading ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyse en cours…</>
                            ) : (
                                <><Sparkles className="h-4 w-4 mr-2" />{c("dashboard.ai.cta", "Lancer l'analyse")}</>
                            )}
                        </Button>
                        {insights && (
                            <div className="mt-5 bg-white/10 rounded-2xl p-4 text-sm whitespace-pre-line leading-relaxed" data-testid="ai-insights-content">
                                {insights}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* BILL ANALYSES HISTORY */}
            <Card className="rounded-3xl border-border mt-6" data-testid="bills-card">
                <CardContent className="p-7">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                        <div>
                            <p className="em-eyebrow flex items-center gap-2"><ScanLine className="h-4 w-4" />{c("dashboard.bills.eyebrow", "Mes factures analysées")}</p>
                            <h3 className="font-display text-xl font-bold text-primary mt-1">{c("dashboard.bills.title_prefix", "Historique")} · {bills.length} analyse{bills.length > 1 ? "s" : ""}</h3>
                        </div>
                        <Button asChild size="sm" className="rounded-full bg-primary hover:bg-primary/90" data-testid="upload-new-bill-btn">
                            <Link to="/comparateur">
                                <FileImage className="h-4 w-4 mr-2" />
                                {c("dashboard.bills.upload_cta", "Analyser une nouvelle facture")}
                            </Link>
                        </Button>
                    </div>
                    {billsLoading ? (
                        <p className="text-muted-foreground" data-testid="bills-loading">Chargement…</p>
                    ) : bills.length === 0 ? (
                        <div className="bg-secondary rounded-2xl p-10 text-center text-muted-foreground" data-testid="bills-empty">
                            <FileImage className="h-10 w-10 mx-auto text-primary/40" />
                            <p className="mt-3">{c("dashboard.bills.empty_title", "Aucune facture analysée")}</p>
                            <p className="text-sm">{c("dashboard.bills.empty_body", "Téléversez une facture pour que le moteur en extraie automatiquement vos données contractuelles.")}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="bills-list">
                            {bills.map((b) => {
                                const conf = b.confidence === "high" ? "bg-emerald-500" : b.confidence === "medium" ? "bg-amber-500" : "bg-rose-500";
                                const useLink = `/comparateur?${new URLSearchParams({
                                    ...(b.annual_kwh ? { kwh: String(Math.round(b.annual_kwh)) } : {}),
                                    ...(b.current_price_kwh ? { price: String(b.current_price_kwh) } : {}),
                                    ...(b.current_subscription_monthly ? { sub: String(b.current_subscription_monthly) } : {}),
                                    ...(b.energy_type ? { type: b.energy_type } : {}),
                                }).toString()}`;
                                return (
                                    <div key={b.id} className="border border-border rounded-2xl p-4 hover:border-primary/40 transition-colors" data-testid={`bill-${b.id}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-display font-bold text-primary truncate">
                                                        {b.supplier_name || c("dashboard.bills.unknown_supplier", "Fournisseur inconnu")}
                                                    </span>
                                                    <span className={`text-[10px] uppercase tracking-wider font-bold text-white px-2 py-0.5 rounded-full ${conf}`} data-testid={`bill-conf-${b.id}`}>
                                                        {b.confidence}
                                                    </span>
                                                    {b.energy_type === "gas" ? (
                                                        <Flame className="h-3.5 w-3.5 text-primary/60" />
                                                    ) : (
                                                        <Zap className="h-3.5 w-3.5 text-primary/60" />
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    {new Date(b.created_at).toLocaleDateString("fr-FR", { dateStyle: "medium" })}
                                                    {b.file_name && <span className="truncate"> · {b.file_name}</span>}
                                                </p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                type="button"
                                                onClick={() => deleteBill(b.id)}
                                                data-testid={`bill-delete-${b.id}`}
                                                className="-mr-2"
                                            >
                                                <Trash2 className="h-4 w-4 text-destructive" />
                                            </Button>
                                        </div>
                                        <div className="grid grid-cols-3 gap-2 mt-3">
                                            <Stat label={c("dashboard.bills.stat_kwh", "Conso/an")} value={b.annual_kwh ? `${Math.round(b.annual_kwh).toLocaleString("fr-FR")} kWh` : "—"} />
                                            <Stat label={c("dashboard.bills.stat_price", "Prix kWh")} value={b.current_price_kwh ? `${Number(b.current_price_kwh).toFixed(4)} €` : "—"} />
                                            <Stat label={c("dashboard.bills.stat_sub", "Abonnt")} value={b.current_subscription_monthly ? `${Number(b.current_subscription_monthly).toFixed(2)} €` : "—"} />
                                        </div>
                                        {(b.annual_kwh || b.current_price_kwh) && (
                                            <Button asChild variant="outline" size="sm" className="w-full rounded-full mt-3 border-primary/30 text-primary hover:bg-primary/10" data-testid={`bill-use-${b.id}`}>
                                                <Link to={useLink}>
                                                    <BadgeCheck className="h-3.5 w-3.5 mr-1.5" />
                                                    {c("dashboard.bills.compare_cta", "Comparer avec ces données")}
                                                </Link>
                                            </Button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* QUIZ HISTORY */}
            <Card className="rounded-3xl border-border mt-6" data-testid="quiz-history-card">
                <CardContent className="p-7">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                        <div>
                            <p className="em-eyebrow flex items-center gap-2"><Trophy className="h-4 w-4" />{c("dashboard.quiz.eyebrow", "Mes recommandations")}</p>
                            <h3 className="font-display text-xl font-bold text-primary mt-1">{c("dashboard.quiz.title", "Historique des quiz fournisseurs")}</h3>
                        </div>
                        <Button asChild size="sm" className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-quiz-btn">
                            <Link to="/fournisseurs">
                                <Sparkles className="h-4 w-4 mr-2" />
                                {c("dashboard.quiz.new_cta", "Nouveau quiz")}
                            </Link>
                        </Button>
                    </div>
                    {historyLoading ? (
                        <p className="text-muted-foreground" data-testid="history-loading">Chargement…</p>
                    ) : history.length === 0 ? (
                        <div className="bg-secondary rounded-2xl p-10 text-center text-muted-foreground" data-testid="history-empty">
                            <Trophy className="h-10 w-10 mx-auto text-primary/40" />
                            <p className="mt-3">{c("dashboard.quiz.empty_title", "Aucun quiz enregistré")}</p>
                            <p className="text-sm">{c("dashboard.quiz.empty_body", "Faites votre premier quiz personnalisé pour trouver le fournisseur le plus aligné avec vos priorités.")}</p>
                        </div>
                    ) : (
                        <div className="space-y-3" data-testid="history-list">
                            {history.map((h) => (
                                <div
                                    key={h.id}
                                    className="border border-border rounded-2xl p-5"
                                    data-testid={`history-${h.id}`}
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                <Calendar className="h-3 w-3" />
                                                {new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })}
                                            </div>
                                            <div className="flex flex-wrap gap-1.5 mt-2">
                                                <span className="text-xs bg-secondary px-2 py-1 rounded-full">
                                                    {h.profile?.audience === "b2b" ? "Pro" : "Particulier"}
                                                </span>
                                                <span className="text-xs bg-secondary px-2 py-1 rounded-full">
                                                    {h.profile?.energy_type === "gas" ? "Gaz" : h.profile?.energy_type === "both" ? "Élec + Gaz" : "Électricité"}
                                                </span>
                                                <span className="text-xs bg-secondary px-2 py-1 rounded-full capitalize">
                                                    Priorité {h.profile?.priority}
                                                </span>
                                                <span className="text-xs bg-secondary px-2 py-1 rounded-full">
                                                    Écolo {h.profile?.green_preference}
                                                </span>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            type="button"
                                            onClick={() => deleteHistory(h.id)}
                                            data-testid={`history-delete-${h.id}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                                        {(h.top || []).slice(0, 3).map((t, idx) => (
                                            <Link
                                                key={t.supplier_id}
                                                to={`/fournisseurs/${t.supplier_id}`}
                                                className={`rounded-xl p-3 transition-colors block ${
                                                    idx === 0 ? "bg-accent text-accent-foreground hover:bg-accent/90" : "bg-secondary hover:bg-muted"
                                                }`}
                                                data-testid={`history-rec-${h.id}-${t.supplier_id}`}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-bold tracking-wide">
                                                        #{idx + 1}{idx === 0 ? " · TOP" : ""}
                                                    </span>
                                                    <span className="font-mono font-bold text-sm">{t.match_score ?? "?"}%</span>
                                                </div>
                                                <p className="font-display font-bold text-base mt-1">{t.supplier_name || t.supplier_id}</p>
                                                <p className={`text-xs mt-1 line-clamp-2 ${idx === 0 ? "text-accent-foreground/80" : "text-muted-foreground"}`}>
                                                    {t.reason}
                                                </p>
                                            </Link>
                                        ))}
                                    </div>
                                    {h.advice && (
                                        <details className="mt-3 group">
                                            <summary className="text-xs text-primary font-semibold cursor-pointer hover:underline list-none flex items-center gap-1">
                                                {c("dashboard.quiz.advice_toggle", "Voir le conseil")} <ArrowRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                                            </summary>
                                            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{h.advice}</p>
                                        </details>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* TABLE */}
            <Card className="rounded-3xl border-border mt-6">
                <CardContent className="p-7">
                    <div className="flex items-center justify-between mb-5">
                        <h3 className="font-display text-xl font-bold text-primary">{c("dashboard.table.title", "Toutes mes mesures")}</h3>
                        <span className="text-sm text-muted-foreground">{items.length} mesure(s)</span>
                    </div>
                    {loading ? (
                        <p className="text-muted-foreground" data-testid="dashboard-loading">Chargement…</p>
                    ) : items.length === 0 ? (
                        <div className="bg-secondary rounded-2xl p-10 text-center text-muted-foreground" data-testid="dashboard-empty">
                            {c("dashboard.table.empty", "Aucune mesure pour l'instant. Cliquez sur \"Ajouter une mesure\" pour commencer.")}
                        </div>
                    ) : (
                        <div className="overflow-x-auto" data-testid="consumption-table">
                            <table className="w-full min-w-[600px]">
                                <thead className="text-left">
                                    <tr>
                                        <th className="em-eyebrow py-3">{c("dashboard.table.col_month", "Mois")}</th>
                                        <th className="em-eyebrow py-3">{c("dashboard.table.col_type", "Type")}</th>
                                        <th className="em-eyebrow py-3">{c("dashboard.table.col_kwh", "kWh")}</th>
                                        <th className="em-eyebrow py-3">{c("dashboard.table.col_cost", "Coût")}</th>
                                        <th className="em-eyebrow py-3 text-right">{c("dashboard.table.col_action", "Action")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((i) => (
                                        <tr key={i.id} className="border-t border-border" data-testid={`consumption-row-${i.id}`}>
                                            <td className="py-3 font-medium">{i.month}</td>
                                            <td className="py-3">
                                                {i.energy_type === "electricity" ? (
                                                    <span className="inline-flex items-center gap-1"><Zap className="h-4 w-4" />Élec.</span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1"><Flame className="h-4 w-4" />Gaz</span>
                                                )}
                                            </td>
                                            <td className="py-3 font-mono">{i.kwh}</td>
                                            <td className="py-3 font-mono">{i.cost_eur.toFixed(2)} €</td>
                                            <td className="py-3 text-right">
                                                <Button variant="ghost" size="sm" onClick={() => deleteItem(i.id)} data-testid={`delete-${i.id}`}>
                                                    <Trash2 className="h-4 w-4 text-destructive" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-secondary rounded-lg p-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
            <p className="font-mono text-xs font-bold text-primary mt-0.5 truncate">{value}</p>
        </div>
    );
}
